import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiPlus, HiCalendarDays } from "react-icons/hi2";
import BottomNav from "../components/layout/BottomNav";
import Header from "../components/layout/Header";
import { useAuth } from "../contexts/AuthContext";
import {
  AthleteProgram,
  programDetailPath,
  programService,
} from "../services/programService";
import { useAthletePrograms } from "../hooks/useAthletePrograms";
import ProgramCard from "../components/programs/ProgramCard";

/**
 * Athlete Programs list (plan 3g): coach-assigned programs derived from the
 * athlete's sessions plus the athlete's own programs, each with assigner,
 * weeks, duration, progress and current week. Cards open the read-only
 * detail; self programs keep Edit (builder) and Delete.
 */
const Programs: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { programs, loading, error: loadError, reload } = useAthletePrograms(
    currentUser?.uid,
  );
  const [deleting, setDeleting] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const assigned = programs.filter((p) => p.source === "organisation");
  const own = programs.filter((p) => p.source === "self");
  const error = actionError ?? loadError;

  const handleDelete = async (program: AthleteProgram) => {
    if (!currentUser?.uid) return;
    const confirmed = window.confirm(
      `Delete "${program.name}"? All linked sessions will also be removed.`,
    );
    if (!confirmed) return;
    try {
      setDeleting(program.id);
      setActionError(null);
      await programService.deleteProgram(currentUser.uid, program.id);
      await reload();
    } catch (deleteError) {
      console.error("Error deleting program:", deleteError);
      setActionError("Failed to delete program");
    } finally {
      setDeleting(null);
    }
  };

  const renderSection = (
    title: string,
    subtitle: string,
    items: AthleteProgram[],
  ) => (
    <section className="mb-8">
      <div className="mb-3">
        <h2 className="text-sm font-heading text-base-content">
          {title}{" "}
          <span className="font-grotesk text-base-content/60">{subtitle}</span>
        </h2>
      </div>
      <div className="space-y-3">
        {items.map((program) => (
          <ProgramCard
            key={`${program.source}:${program.id}`}
            program={program}
            onOpen={() => navigate(programDetailPath(program))}
            onEdit={
              program.source === "self"
                ? () => navigate(`/programs/${program.id}`)
                : undefined
            }
            onDelete={
              program.source === "self"
                ? () => handleDelete(program)
                : undefined
            }
            deleting={deleting === program.id}
          />
        ))}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-base-200">
      <Header />
      <main className="max-w-3xl mx-auto pt-24 pb-28 px-6 sm:px-0">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-base-content font-heading">
              Programs
            </h1>
            <p className="mt-2 text-sm text-base-content/60 font-grotesk">
              Multi-week training blocks assigned by your coach or built by
              you.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/programs/new")}
            aria-label="New Program"
            title="New Program"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-content hover:bg-primary/90 transition-colors flex-shrink-0"
          >
            <HiPlus className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            className="mb-6 rounded-xl border border-error/30 bg-error/10 px-4 py-3 text-sm text-error font-grotesk"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Loading skeleton */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl border border-base-300 bg-base-100 p-5"
              >
                <div className="space-y-2">
                  <div className="h-5 bg-base-300 rounded w-1/3" />
                  <div className="h-4 bg-base-300 rounded w-1/2" />
                  <div className="h-2 bg-base-300 rounded w-full mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : programs.length === 0 ? (
          /* Empty state */
          <div className="rounded-xl border-2 border-dashed border-base-300 bg-base-100 p-12 text-center">
            <HiCalendarDays className="text-4xl text-base-content/30 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-base-content font-heading">
              No programs yet
            </h2>
            <p className="mt-2 text-sm text-base-content/60 font-grotesk max-w-sm mx-auto">
              Programs your coach assigns will appear here. You can also build
              your own multi-week block of sessions.
            </p>
            <button
              type="button"
              onClick={() => navigate("/programs/new")}
              className="btn btn-neutral mt-6 min-h-11 h-11 rounded-xl px-5 text-sm font-medium font-grotesk normal-case"
            >
              <HiPlus className="text-base" />
              Create Program
            </button>
          </div>
        ) : (
          <>
            {assigned.length > 0 &&
              renderSection("Assigned", "to you", assigned)}
            {own.length > 0 && renderSection("Your", "programs", own)}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Programs;
